import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AppConfig } from "../config/app.config";

import { api } from "./ApiClient";

import { CookiesHelper } from "../helpers/cookies.helper";
import { AuthHelper } from "../helpers/auth.helper";

import { EtyMBTSConsent } from "../types/entities.interface";
import { HMICreateConsent, HMIUpdatePilotContact } from "../types/services.interfaces/mbts_consents";

export const useGetConsentDetails = () => {
    AuthHelper.presetPrivateRequest(api);

    const consentToken = CookiesHelper.get(AppConfig.cookieTokenNames.mbtsConsent);

    const queryFn = async (): Promise<EtyMBTSConsent> => {
        const res = await api.get(`/mbts-consents/details/${consentToken}`);
        return res.data;
    };

    return useQuery({
        queryKey: ['mbts-consents-details', consentToken],
        queryFn,
        enabled: !!consentToken,
    });
};

export const useCreateConsent = () => {
    AuthHelper.presetPrivateRequest(api);

    const mutationFn = async (data: HMICreateConsent): Promise<string> => {
        const res = await api.post('/mbts-consents/create', data.payload);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsProcessing(true);
            variables.setError(null);
        },
        onSuccess: (token, variables) => {
            CookiesHelper.set(AppConfig.cookieTokenNames.mbtsConsent, token, (24 * 60 * 60 * 1000));
            variables.setIsSuccess(true);
        },
        onError: (error: any, variables) => {
            variables.setError(error?.response?.data?.message ?? 'Something went wrong.');
        },
        onSettled: (_, __, variables) => {
            variables.setIsProcessing(false);
        },
    });
};

export const useUpdatePilotContact = () => {
    AuthHelper.presetPrivateRequest(api);

    const queryClient = useQueryClient();

    const mutationFn = async (data: HMIUpdatePilotContact): Promise<void> => {
        const res = await api.patch(`/mbts-consents/update-pilot-contact`, data.payload);
        return res.data;
    };

    return useMutation({
        mutationFn,
        onMutate: (variables) => {
            variables.setIsProcessing(true);
            variables.setError(null);
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['mbts-consents-details'] });
            variables.setIsSuccess(true);
        },
        onError: (error: any, variables) => {
            variables.setError(error?.response?.data?.message ?? 'Something went wrong.');
        },
        onSettled: (_, __, variables) => {
            variables.setIsProcessing(false);
        },
    });
};